<template>
  <div>
    <validation-observer
      v-slot="{ handleSubmit }"
      ref="refFormObserver"
    >
      <b-form
        @submit.prevent="handleSubmit(onSubmit)"
        @reset.prevent="resetForm"
      >
        <!-- Cliente (desabilitado) -->
        <b-form-group
          label="Cliente"
          label-for="cliente"
        >
          <b-form-input
            id="cliente"
            :value="clienteNome"
            disabled
            class="bg-light"
          />
        </b-form-group>

        <!-- Data de Agendamento -->
        <validation-provider
          v-slot="validationContext"
          name="Data de Agendamento"
          rules="required"
        >
          <b-form-group
            label="Data e Hora *"
            label-for="data-agendamento"
            :state="getValidationState(validationContext)"
          >
            <flat-pickr
              v-model="form.data_agendamento"
              class="form-control"
              :config="{
                enableTime: true,
                dateFormat: 'd/m/Y H:i',
                time_24hr: true,
                locale: 'pt',
                minDate: new Date(Date.now() + 60000),
              }"
              placeholder="Selecione a data e hora (DD/MM/YYYY HH:mm)"
            />
            <b-form-invalid-feedback :state="getValidationState(validationContext)">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <!-- Executivo (condicional) -->
        <b-form-group
          v-if="isNotExecutivoCobranca"
          label="Executivo"
          label-for="executivo"
        >
          <b-form-select
            id="executivo"
            v-model="form.atendente_id"
            :options="executivoSelectOptions"
          />
        </b-form-group>

        <!-- Observações -->
        <b-form-group
          label="Observações"
          label-for="observacoes"
        >
          <b-form-textarea
            id="observacoes"
            v-model="form.observacoes"
            placeholder="Observações do agendamento..."
            rows="3"
          />
        </b-form-group>

        <!-- Status -->
        <b-form-group
          label="Status"
          label-for="status"
        >
          <b-form-select
            id="status"
            v-model="form.status"
            :options="statusSelectOptions"
          />
        </b-form-group>

        <!-- Motivo -->
        <b-form-group
          label="Motivo"
          label-for="motivo"
        >
          <b-form-select
            id="motivo"
            v-model="form.motivo"
            :options="motivoSelectOptions"
          />
        </b-form-group>

        <!-- Botões -->
        <div class="d-flex justify-content-end mt-2">
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mr-1"
            @click="resetForm"
          >
            Limpar
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            type="submit"
            :disabled="loading"
          >
            <b-spinner
              v-if="loading"
              small
            />
            Criar Agendamento
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import {
  BForm, BFormGroup, BFormInput, BFormTextarea, BFormSelect,
  BButton, BFormInvalidFeedback, BSpinner,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'
import formValidation from '@core/comp-functions/forms/form-validation'
import { ref, computed, onMounted } from '@vue/composition-api'
import agendamentosService from '@/services/agendamentos'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { statusOptions, motivoOptions } from '@/views/apps/calendar-pessoal/calendar-utils'

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BButton,
    flatPickr,
    ValidationProvider,
    BFormInvalidFeedback,
    ValidationObserver,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  props: {
    clienteId: {
      type: Number,
      required: true,
    },
    clienteNome: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    extend('required', required)

    const { getValidationState } = formValidation()
    const toast = useToast()
    const refFormObserver = ref()

    const form = ref({
      data_agendamento: '',
      atendente_id: null,
      observacoes: '',
      cliente_id: props.clienteId,
      tipo: 'NEGOCIACAO',
      status: 'AGENDADO',
      motivo: '',
    })

    const loading = ref(false)
    const executivosList = ref([])

    const isNotExecutivoCobranca = computed(() => {
      const funcao = localStorage.getItem('funcao')
      return funcao !== 'Executivo de Cobrança'
    })

    const executivoSelectOptions = computed(() => [
      { value: null, text: 'Selecione o executivo', disabled: true },
      ...executivosList.value.map(e => ({ value: e.id, text: e.nome || e.name })),
    ])

    const statusSelectOptions = computed(() => [
      { value: '', text: 'Selecione o status', disabled: true },
      ...statusOptions.map(s => ({ value: s.value, text: s.label })),
    ])

    const motivoSelectOptions = computed(() => [
      { value: '', text: 'Selecione o motivo (opcional)' },
      ...motivoOptions.map(m => ({ value: m.value, text: m.label })),
    ])

    const fetchExecutivos = async () => {
      try {
        const axiosAuth = (await import('@/../axios-auth')).default
        const response = await axiosAuth.get('api/v1/executivo')
        const data = response.data || []
        executivosList.value = Array.isArray(data) ? data : []
      } catch {
        try {
          const axiosGlobal = (await import('@/libs/axios')).default
          const response = await axiosGlobal.get('api/v1/executivo')
          const data = response.data || []
          executivosList.value = Array.isArray(data) ? data : []
        } catch { /* ignore */ }
      }
    }

    onMounted(() => {
      if (isNotExecutivoCobranca.value) {
        fetchExecutivos()
      }
    })

    const resetForm = () => {
      form.value = {
        data_agendamento: '',
        atendente_id: null,
        observacoes: '',
        cliente_id: props.clienteId,
        tipo: 'NEGOCIACAO',
        status: 'AGENDADO',
        motivo: '',
      }
    }

    const onSubmit = async () => {
      loading.value = true
      try {
        let dataISO = ''
        if (form.value.data_agendamento) {
          const [datePart, timePart] = form.value.data_agendamento.split(' ')
          const [day, month, year] = datePart.split('/')
          const [hour, minute] = timePart.split(':')
          const dataObj = new Date(year, month - 1, day, hour, minute, 0)
          dataISO = dataObj.toISOString()
        }

        const atendenteId = parseInt(localStorage.getItem('executivo_id') || localStorage.getItem('funcionarioId')) || null

        const dados = {
          ...form.value,
          data_agendamento: dataISO,
          atendente_id: isNotExecutivoCobranca.value ? form.value.atendente_id : atendenteId,
        }

        await agendamentosService.criarAgendamento(dados)
        toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            icon: 'CheckIcon',
            text: 'Agendamento criado com sucesso!',
            variant: 'success',
          },
        })
        resetForm()
        refFormObserver.value.reset()
      } finally {
        loading.value = false
      }
    }

    return {
      form,
      loading,
      onSubmit,
      resetForm,
      getValidationState,
      refFormObserver,
      isNotExecutivoCobranca,
      executivoSelectOptions,
      statusSelectOptions,
      motivoSelectOptions,
    }
  },
}
</script>
